// =============================================================================
// RunReportScreen (/meldingen) — an external partner reports a faulty run
// =============================================================================
// The Evoluon side of per-run billing. Their operators (role evo_operator)
// see the runs of their own room for one month: start time, PC and title,
// nothing else. No duration, no completion, no restart flag: the reporter
// says what happened and the reviewer holds the evidence. One report per
// run, free text plus an optional photo. A report is accepted on filing, so the
// run is not invoiced, unless the run was invoiced already: then it waits for a
// manager. A later rejection and its note show up on the same card.
//
// Gated by booking_report_external_runs (route + every RPC); rooms are
// scoped server-side to the operator's location.
import { useCallback, useEffect, useMemo, useState, type FormEvent } from 'react';
import {
  createRunReport,
  listReportRooms,
  listReportRuns,
  uploadReportPhoto,
  type ReporterRoom,
  type ReporterRun,
  type RunReportStatus,
} from '../lib/runs';
import { formatError } from '../lib/errors';
import { venueMonthBounds, venueYearMonth } from '../lib/time';
import { RunReportCard } from '../components/RunReportCard';
import { ReportIntro, ReportSortField, ReporterRunDays, useReportSort } from '../components/ReporterRuns';

const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp';

export function RunReportScreen() {
  const [rooms, setRooms] = useState<ReporterRoom[]>([]);
  const [roomId, setRoomId] = useState('');
  const [month, setMonth] = useState(() => venueYearMonth());
  const [sort, changeSort] = useReportSort();
  const [runs, setRuns] = useState<ReporterRun[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listReportRooms()
      .then((rs) => {
        if (cancelled) return;
        setRooms(rs);
        setRoomId((cur) => cur || rs[0]?.room_id || '');
        if (rs.length === 0) setLoading(false);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(formatError(e));
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const bounds = useMemo(() => (month ? venueMonthBounds(month) : null), [month]);

  const load = useCallback(async () => {
    if (!roomId || !bounds) return;
    setLoading(true);
    try {
      setRuns(await listReportRuns(roomId, bounds.startUtc, bounds.endUtc));
      setError(null);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setLoading(false);
    }
  }, [roomId, bounds]);

  useEffect(() => {
    void load();
  }, [load]);

  const room = rooms.find((r) => r.room_id === roomId) ?? null;

  return (
    <div className="screen report-screen">
      <h1 className="payments-title">Meldingen</h1>
      <ReportIntro />

      <div className="payments-filters">
        {rooms.length > 1 ? (
          <label className="walkin-field payments-filter">
            <span>Ruimte</span>
            <select value={roomId} onChange={(e) => setRoomId(e.target.value)}>
              {rooms.map((r) => (
                <option key={r.room_id} value={r.room_id}>
                  {r.customer_name} · {r.room_name}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <label className="walkin-field payments-filter">
          <span>Maand</span>
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </label>
        <ReportSortField sort={sort} onChange={changeSort} />
      </div>

      {notice ? <div className="muted runs-notice">{notice}</div> : null}
      {error ? <div className="error-card">{error}</div> : null}
      {loading ? <div className="muted">Laden…</div> : null}

      {!loading && rooms.length === 0 && !error ? (
        <div className="muted vouchers-empty">
          Er is geen ruimte aan dit account gekoppeld. Vraag Neoseum om de locatie te zetten.
        </div>
      ) : null}

      {!loading && runs && runs.length === 0 ? (
        <div className="muted vouchers-empty">Geen runs in deze maand.</div>
      ) : null}

      {runs && runs.length > 0 && room ? (
        <ReporterRunDays
          runs={runs}
          sort={sort}
          renderBody={(run) =>
            run.report ? (
              <RunReportCard report={run.report} />
            ) : openId === run.id ? (
              <ReportForm
                run={run}
                roomId={room.room_id}
                onCancel={() => setOpenId(null)}
                onFiled={async (status) => {
                  setOpenId(null);
                  setNotice(
                    status === 'open'
                      ? 'Melding verstuurd. Deze run is al gefactureerd, dus Neoseum beoordeelt de melding eerst.'
                      : 'Melding verstuurd. Deze run wordt niet gefactureerd.',
                  );
                  await load();
                }}
              />
            ) : (
              <button
                type="button"
                className="btn-link"
                onClick={() => {
                  setOpenId(run.id);
                  setNotice(null);
                }}
              >
                Fout melden
              </button>
            )
          }
        />
      ) : null}
    </div>
  );
}

function ReportForm({
  run,
  roomId,
  onCancel,
  onFiled,
}: {
  run: ReporterRun;
  roomId: string;
  onCancel: () => void;
  onFiled: (status: RunReportStatus) => Promise<void>;
}) {
  const [message, setMessage] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // jsdom (tests) has no object URLs; the preview is a nicety, not the upload.
    if (!file || typeof URL.createObjectURL !== 'function') {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const text = message.trim();
    if (!text) return;
    setBusy(true);
    setError(null);
    try {
      const photoPath = file ? await uploadReportPhoto(roomId, run.id, file) : null;
      await onFiled(await createRunReport(run.id, text, photoPath));
    } catch (err) {
      setError(formatError(err));
      setBusy(false);
    }
  }

  return (
    <form className="report-form" onSubmit={(e) => void submit(e)}>
      <label className="walkin-field">
        <span>Wat ging er mis?</span>
        <textarea
          autoFocus
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Bijv. bezoeker zat vast bij de trap, opnieuw gestart"
          disabled={busy}
          required
        />
      </label>
      <label className="report-form-photo">
        <span className="muted">Foto (optioneel)</span>
        <input
          type="file"
          accept={PHOTO_ACCEPT}
          disabled={busy}
          onChange={(e) => setFile(e.target.files?.[0] ?? null)}
        />
        {preview ? <img className="report-form-preview" src={preview} alt="Gekozen foto" /> : null}
      </label>
      {error ? <div className="error-card">{error}</div> : null}
      <div className="report-form-actions">
        <button type="submit" className="btn-primary" disabled={busy || message.trim() === ''}>
          {busy ? 'Versturen…' : 'Verstuur melding'}
        </button>
        <button type="button" className="btn-link" onClick={onCancel} disabled={busy}>
          Annuleer
        </button>
      </div>
    </form>
  );
}
